import { SearchSuggestionList } from "@/components/layout/search-suggestions";
import {
  SEARCH_PLACEHOLDER,
  useSearchSuggestions
} from "@/components/layout/useSearchSuggestions";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { ArrowLeft, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Phone search, as a page.
 *
 * The shape is the whole point. This was a portalled `Drawer` twice, and both
 * times a real phone broke it in a way jsdom and headless Chromium could not
 * see (storefront#158): `position: fixed` resolves against the *layout*
 * viewport, which the on-screen keyboard does not shrink, so Safari panned the
 * pinned panel and carried the input off the top; and Radix's scroll lock locks
 * `body`, which this app never scrolls, so the first gesture chained into the
 * shell behind. A page dismisses by the hardware back button for free, and has
 * nothing pinned for Safari to pan.
 *
 * The layout is a viewport-tall flex column: the search bar is a non-scrolling
 * child that is *always visible*, and the suggestion list is the single scroller
 * below it. It was document-scroll + a `sticky` bar before, but a real phone
 * showed the bar failing to pin (storefront#145): `sticky` against the
 * *document*, inside a `min-h-dvh` flex column, is an iOS Safari weak spot, and
 * the whole point of this screen is that the field stays put while the
 * suggestions move. A dedicated inner scroller pins the header deterministically
 * instead of leaning on that.
 *
 * The column is sized to the *visual* viewport, not `100dvh`. The keyboard is up
 * the whole time this page is open, and iOS Safari does not shrink the viewport
 * for it - `dvh`/`vh`/`svh`/`lvh` all track the URL bar, not the keyboard - so a
 * `h-dvh` column keeps its full height and the last suggestions sit behind the
 * keyboard where no amount of scrolling reaches them (storefront#145 device
 * testing). `window.visualViewport.height` *does* exclude the keyboard, so the
 * column follows it; `h-dvh` stays only as the fallback before the first
 * measurement and where there is no `visualViewport` (jsdom, old engines).
 *
 * The inner `overflow-y-auto` is what the drawer could not have - a nested
 * scroller was the gesture it chained out of. A *page* has nothing behind it to
 * chain into, and `overscroll-contain` closes even that; the reason the drawer
 * forbade one does not survive the move to a route. What does survive: nothing
 * here may be `position: fixed` or a portal - that is still the defect.
 */
export function SearchInput() {
  const navigate = useNavigate();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  // Always open: unlike the dropdown there is no closed state to represent -
  // arriving here *is* the open gesture, and leaving is a navigation. It
  // replaces itself on the way to the results so Back from them returns to
  // wherever search was opened from, not to this page with the keyboard up.
  const search = useSearchSuggestions({ active: true, replace: true });

  // Raise the keyboard on arrival. Focusing in an effect rather than via the
  // `autoFocus` attribute is deliberate: `autoFocus` fires during commit,
  // before the browser has laid the page out, and iOS Safari then has nothing
  // stable to scroll the caret into view against.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Height of the area the keyboard is *not* covering - see the note on the
  // component. `undefined` until measured (and where there is no
  // `visualViewport`), so the `h-dvh` fallback holds. `resize` fires as the
  // keyboard opens and closes; `scroll` covers the iOS versions that move the
  // visual viewport without a resize. Same value in re-writes a no-op.
  const [visibleHeight, setVisibleHeight] = useState<number>();
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const sync = () => setVisibleHeight(viewport.height);
    sync();
    viewport.addEventListener("resize", sync);
    viewport.addEventListener("scroll", sync);
    return () => {
      viewport.removeEventListener("resize", sync);
      viewport.removeEventListener("scroll", sync);
    };
  }, []);

  // Back to wherever search was opened from. The fallback matters:
  // `/search/input` is a real URL, so it can be opened cold from a reload or a
  // shared link, and `history.back()` would then leave the app entirely.
  const dismiss = () => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    navigate({ to: "/" });
  };

  return (
    <div
      className="flex h-dvh flex-col bg-white dark:bg-black"
      style={visibleHeight ? { height: visibleHeight } : undefined}
    >
      {/* Always visible: a non-scrolling flex child, so it pins without
          `position` at all - see the note on the component above. */}
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-2 py-2 dark:border-gray-800 dark:bg-black">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close search"
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-zinc-900"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="relative min-w-0 flex-1">
          <input
            ref={inputRef}
            // `type="search"`, not `text`: a text field makes iOS offer its
            // password / card / contact AutoFill bar, which floats over the
            // suggestions and hides them (storefront#145). `autoComplete="off"`
            // does not suppress that bar on its own - the type is what does.
            // `inputMode`/`enterKeyHint` turn the on-screen return key into a
            // Search key, which is what it submits.
            type="search"
            inputMode="search"
            enterKeyHint="search"
            name="search"
            aria-label="Search"
            placeholder={SEARCH_PLACEHOLDER}
            value={search.inputValue}
            onChange={(e) => search.setInputValue(e.target.value)}
            onKeyDown={search.handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            // `text-base` is not decoration: iOS Safari zooms the page in when
            // a control smaller than 16px takes focus, and never zooms back
            // out. `main.css` enforces this on coarse pointers globally; it is
            // stated again here because this is the one field on the app's only
            // keyboard-first screen. `leading-none` sizes the caret to the text
            // rather than to the taller `text-base` line-box, which iOS draws
            // low and off-centre. The `-webkit-search-cancel-button` reset drops
            // the native clear glyph `type="search"` adds - there is already a
            // custom clear button beside it.
            className="h-11 w-full rounded-lg border border-gray-200 bg-gray-50 pl-3 pr-10 text-base leading-none text-gray-900 placeholder:text-gray-500 focus:border-gray-300 focus:outline-none dark:border-gray-800 dark:bg-zinc-900 dark:text-gray-100 [&::-webkit-search-cancel-button]:appearance-none"
          />
          {search.inputValue.length > 0 && (
            <button
              type="button"
              onClick={() => {
                search.clearQuery();
                inputRef.current?.focus();
              }}
              aria-label="Clear search"
              className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* The only scroller. `overscroll-contain` stops a scroll gesture from
          chaining past it - the drawer's old failure mode - though a page has
          nothing behind it to chain into anyway. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {search.actions && (
          <SearchSuggestionList
            actions={search.actions}
            highlightedIndex={search.highlightedIndex}
            onHighlight={search.setHighlightedIndex}
            onSelect={search.selectAction}
            // This surface only exists for coarse pointers, so the touch row
            // height is a constant here rather than a media query.
            isTouch
          />
        )}
      </div>
    </div>
  );
}
